import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Trash2, Clock } from 'lucide-react';
import { getErrorMessage } from '@/lib/utils';


const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_BASE}/api`;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function DoctorTimeSlots() {
  const { token } = useAuth();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newSlot, setNewSlot] = useState({
    day_of_week: 'Monday',
    start_time: '',
    end_time: '',
    max_appointments: 1,
  });

  const fetchSlots = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const userId = response.data.id;
      
      const slotsResponse = await axios.get(`${API}/time-slots/doctor/${userId}`);
      setSlots(slotsResponse.data);
    } catch (error) {
      toast.error('Failed to fetch time slots');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  const handleAddSlot = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/time-slots`, newSlot, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Time slot added successfully');
      setIsDialogOpen(false);
      setNewSlot({ day_of_week: 'Monday', start_time: '', end_time: '', max_appointments: 1 });
      fetchSlots();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add time slot'));
    }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      await axios.delete(`${API}/time-slots/${slotId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Time slot deleted');
      fetchSlots();
    } catch (error) {
      toast.error('Failed to delete time slot');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-background-dark">Weekly Availability Schedule</h2>
          <p className="text-sm text-slate-600 mt-1">Set your recurring weekly schedule. These slots repeat every week.</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-time-slot-btn" className="rounded-full px-6 bg-primary hover:bg-primary-hover">
              <Plus className="mr-2 h-4 w-4" />
              Add Time Slot
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Weekly Time Slot</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddSlot} className="space-y-4 mt-4">
              <div>
                <Label>Day of Week</Label>
                <Select value={newSlot.day_of_week} onValueChange={(val) => setNewSlot({ ...newSlot, day_of_week: val })}>
                  <SelectTrigger data-testid="slot-day-select" className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS.map((day) => (
                      <SelectItem key={day} value={day}>Every {day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">This slot will repeat every week on this day</p>
              </div>

              <div>
                <Label>Start Time</Label>
                <Input
                  data-testid="slot-start-time-input"
                  type="time"
                  required
                  value={newSlot.start_time}
                  onChange={(e) => setNewSlot({ ...newSlot, start_time: e.target.value })}
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label>End Time</Label>
                <Input
                  data-testid="slot-end-time-input"
                  type="time"
                  required
                  value={newSlot.end_time}
                  onChange={(e) => setNewSlot({ ...newSlot, end_time: e.target.value })}
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label>Max Appointments</Label>
                <Input
                  data-testid="slot-max-appointments-input"
                  type="number"
                  min="1"
                  required
                  value={newSlot.max_appointments}
                  onChange={(e) => setNewSlot({ ...newSlot, max_appointments: parseInt(e.target.value) })}
                  className="mt-1.5"
                />
              </div>

              <Button data-testid="slot-submit-btn" type="submit" className="w-full rounded-full bg-primary hover:bg-primary-hover">
                Add Slot
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {slots.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <p className="text-slate-600">No time slots configured yet. Add your first time slot to start receiving appointments.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {slots.map((slot) => (
            <div key={slot.id} className="bg-white rounded-xl border border-slate-200 p-6" data-testid={`time-slot-${slot.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="h-4 w-4 text-primary" />
                    <h3 className="font-heading font-bold text-lg text-background-dark">Every {slot.day_of_week}</h3>
                  </div>
                  <p className="text-sm font-semibold text-primary">
                    {slot.start_time} - {slot.end_time}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Recurring weekly</p>
                </div>
                <Button
                  data-testid={`delete-slot-${slot.id}-btn`}
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDeleteSlot(slot.id)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="text-sm text-slate-600">
                Max appointments: <span className="font-semibold text-slate-900">{slot.max_appointments}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}